import * as React from 'react';
import * as AWS from 'aws-sdk';
import { useStreamingZipDownload } from '../../../hooks/useStreamingZipDownload';

interface FileKey {
  key: string;
  fileName: string;
  url: string;
}

export interface FolderDownloadButtonProps {
  folderKey: string;
  zipName: string;
  presignUrl: (key: string) => string;
  bucketName: string;
  region?: string;
  onError?: (message: string) => void;
  onComplete?: () => void;
}

export function FolderDownloadButton({
  folderKey,
  zipName,
  presignUrl,
  bucketName,
  region,
  onError,
  onComplete,
}: FolderDownloadButtonProps) {
  const { download, progress, status } = useStreamingZipDownload();
  const [completed, setCompleted] = React.useState(false);

  const msg = (msg: string) => {
    if (/403/.test(msg)) {
      return 'Connection timed out — please check your internet and try again.';
    }
    if (/Nothing to download/.test(msg)) {
      return 'This folder is empty — nothing to download.';
    }
    return 'Something went wrong downloading. Please try again.';
  };

  const handleClick = React.useCallback(async () => {
    setCompleted(false);
    const prefix = folderKey.replace(/\/+$/, '') + '/';

    if (region) AWS.config.update({ region });
    const s3 = new AWS.S3();

    let token: string | undefined;
    const allKeys: string[] = [];

    try {
      do {
        const resp = await s3.listObjectsV2({ Bucket: bucketName, Prefix: prefix, ContinuationToken: token }).promise();

        (resp.Contents || [])
          .map(o => o.Key!)
          .filter(key => key !== prefix && !key.endsWith('/'))
          .forEach(k => allKeys.push(k));

        token = resp.IsTruncated ? resp.NextContinuationToken! : undefined;
      } while (token);

      if (allKeys.length === 0) {
        const fm = msg('Nothing to download');
        onError?.(fm);
        return;
      }
    } catch (e) {
      const fm = msg(e.message);
      onError?.(fm);
      return;
    }

    const files: FileKey[] = allKeys.map(key => ({
      key,
      fileName: key.split('/').pop()!,
      url: presignUrl(key),
    }));

    download({
      files,
      zipName,
      onError: err => onError?.(msg(err.message)),
      onComplete: () => {
        setCompleted(true);
        onComplete?.();
      },
    });
  }, [folderKey, zipName, presignUrl, bucketName, region, download, onError, onComplete]);

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        onClick={handleClick}
        disabled={status === 'running'}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          verticalAlign: 'middle',
        }}
      >
        <svg width="2em" height="2em" viewBox="0 0 16 16" fill="#34495e" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5 a.5.5 0 0 1-1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"
          />
          <path
            fillRule="evenodd"
            d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3 a.5.5 0 0 0-.708-.708L8.5 10.293V1.5 a.5.5 0 0 0-1 0v8.793L5.354 8.146 a.5.5 0 1 0-.708.708l3 3z"
          />
        </svg>
      </button>

      {status === 'running' && (
        <div style={{ width: 120, marginTop: 4 }}>
          <progress value={progress.completed} max={progress.total} style={{ width: '100%' }} />
          <small>
            {progress.completed}/{progress.total}
          </small>
        </div>
      )}

      {completed && (
        <div style={{ color: 'green', marginTop: 4 }}>
          <small>Download complete ✓</small>
        </div>
      )}
    </div>
  );
}
